import { Outlet } from '@tanstack/react-router';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose, map } from 'lodash/fp';
import { Suspense, useRef } from 'react';
import { pipe } from 'fp-ts/function';
import { Container, Grid, GridItem, IconName, Tab, withBreakpointRange } from '@optimus-web/core';
import { ActionType, FeatureFlag, TextKey } from '@mews-commander/enums';
import { DefaultErrorBoundary, isMobile, Loader } from '@mews-commander/ui';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { AsyncMetaState, LocalizedText, useFeatureFlag } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { isFetching as areSpacesFetching } from '@mews-commander/legacy-entities/src/ducks/spaces';
import { isCurrentEnterpriseGroupBillingEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { getReservationGroupName } from '@mews-commander/legacy-entities/src/ducks';
import { useCloseButton } from '../../../../core/windowManager/hooks';
import { changeTab, handleNewReservation } from '../../actions';
import { getSelectedTab, isAnyReservationChecked } from '../../selectors';
import { areReservationsFetching, haveReservationsFailed } from '../../selectors/summary/reservations';
import ModalHeader from '../../../../legacy/React/Components/Common/ModalHeader';
import Warning from '../Warning';
import { ReservationTab } from '../../../../types/reservationTab';
import { HeadingAction } from '../../../../legacy/React/Components/HeadingAction/Action/types';
import { NEW_RESERVATION_BUTTON_DATA_TEST_ID } from '../../../newReservation/constants';
import { ReservationModuleProps } from './types';
import { BulkActionButtons } from './BulkActionButtons';
import {
    BackgroundContainer,
    ScrollableColumnContent,
    ScrollableContentWrapper,
    ContentWrapper,
    BulkActionsWrapper,
    WrapperStack,
    OverflowStackItem,
    ModalWrapper,
} from './styles';
import { StateGroups } from './StateGroups';

interface StateProps extends AsyncMetaState {
    anyChecked: boolean;
    isGroupBillingEnabled: boolean;
    selectedTab?: ReservationTab;
    groupName?: string;
}

const getTabs = (isGroupBillingEnabled: boolean, contractingManualDocumentsEnabled: boolean) =>
    compose(
        map(({ textKey, value }: { textKey: Mews.TextKey; value: ReservationTab }) => (
            <Tab key={value} id={value} label={<LocalizedText textKey={textKey} />} value={value} />
        )),
        compactArray
    )([
        { textKey: TextKey.Status, value: ReservationTab.Status },
        { textKey: TextKey.ReservationProperties, value: ReservationTab.Properties },
        { textKey: TextKey.Group, value: ReservationTab.Group },
        { textKey: TextKey.Pricing, value: ReservationTab.Pricing },
        { textKey: TextKey.Items, value: ReservationTab.Items },
        { textKey: TextKey.Mailing, value: ReservationTab.Mailing },
        { textKey: TextKey.ActionLogItemReport, value: ReservationTab.ActionLog },
        { textKey: TextKey.Summary, value: ReservationTab.Summary },
        isGroupBillingEnabled ? { textKey: TextKey.Billing, value: ReservationTab.Billing } : null,
        contractingManualDocumentsEnabled ? { textKey: TextKey.ContractsTitle, value: ReservationTab.Contracts } : null,
    ]);

const ReservationModule = ({
    reservationId,
    groupId,
    breakpointRange,
    selectedTab,
    anyChecked,
    handleTabClick,
    addNewReservation,
    isGroupBillingEnabled,
    isFetching,
    guideLink,
    groupName,
}: ReservationModuleProps & StateProps & DispatchProps) => {
    const scrollElementRef = useRef<HTMLDivElement>(null);
    const contractingManualDocumentsEnabled = useFeatureFlag(FeatureFlag.contractingManualDocumentsEnabled);

    const isSmall = isMobile(breakpointRange);

    const actions: HeadingAction[] = [
        {
            type: ActionType.Link,
            actionId: 'newReservation',
            text: <LocalizedText textKey={TextKey.NewReservation} />,
            icon: IconName.Add,
            'data-test-icon-button': IconName.Add,
            dataTestId: NEW_RESERVATION_BUTTON_DATA_TEST_ID,
            onClick: addNewReservation,
        },
        {
            type: ActionType.Link,
            actionId: 'help',
            text: <LocalizedText textKey={TextKey.Help} />,
            icon: IconName.Help,
            target: '_blank',
            url: guideLink,
        },
    ];

    const closeButton = useCloseButton();
    const content = anyChecked ? (
        <Suspense fallback={<Loader />}>
            <Outlet />
        </Suspense>
    ) : (
        <Warning textKey={TextKey.ReservationSelectionErrorMessage} />
    );

    const templateColumns = isSmall || isFetching ? '1fr' : '1fr 1fr';
    const gridContent = isFetching ? (
        <Loader />
    ) : (
        <>
            <GridItem overflow="auto">
                <BackgroundContainer>
                    <Container padding="100">{content}</Container>
                </BackgroundContainer>
            </GridItem>

            {!isSmall ? (
                <DefaultErrorBoundary>
                    <WrapperStack vertical>
                        <BulkActionsWrapper>
                            <BulkActionButtons groupId={groupId} />
                        </BulkActionsWrapper>

                        <OverflowStackItem grow ref={scrollElementRef}>
                            <ScrollableContentWrapper>
                                <StateGroups
                                    groupId={groupId}
                                    reservationId={reservationId}
                                    scrollElementRef={scrollElementRef}
                                />
                            </ScrollableContentWrapper>
                        </OverflowStackItem>
                    </WrapperStack>
                </DefaultErrorBoundary>
            ) : null}
        </>
    );

    return (
        <ScrollableColumnContent>
            <ModalWrapper>
                <ModalHeader
                    title={groupName}
                    actions={actions}
                    primaryAction={closeButton}
                    tabsConfig={{
                        activeTabValue: selectedTab,
                        inline: true,
                        handleTabClick: (e, value) => handleTabClick(value as ReservationTab),
                        tabs: getTabs(isGroupBillingEnabled, contractingManualDocumentsEnabled),
                    }}
                />

                <ContentWrapper>
                    <Grid templateColumns={templateColumns} printTemplateColumns="1fr" columnGap="0" height="100%">
                        {gridContent}
                    </Grid>
                </ContentWrapper>
            </ModalWrapper>
        </ScrollableColumnContent>
    );
};

interface DispatchProps {
    handleTabClick: (reservationTab: ReservationTab) => void;
    addNewReservation: () => void;
}

export default pipe(
    ReservationModule,
    withBreakpointRange,
    connect<StateProps, DispatchProps, ReservationModuleProps, ApplicationState>(
        createStructuredSelector<ApplicationState, ReservationModuleProps, StateProps>({
            isFetching: (state, { groupId }) =>
                areReservationsFetching(state, { scope: groupId }) || areSpacesFetching(state),
            hasFailed: (state, { groupId }) => haveReservationsFailed(state, { scope: groupId }),
            selectedTab: (state, { groupId }) => getSelectedTab(state, { scope: groupId }),
            anyChecked: (state, { groupId }) => isAnyReservationChecked(state, { scope: groupId }),
            isGroupBillingEnabled: (state) => isCurrentEnterpriseGroupBillingEnabled(state),
            groupName: (state, { groupId }) => getReservationGroupName(state, { id: groupId }),
        }),
        (dispatch: CommanderThunkDispatch, { groupId }) => ({
            handleTabClick: (reservationTab) => dispatch(changeTab({ reservationTab, scope: groupId })),
            addNewReservation: () => dispatch(handleNewReservation({ groupId })),
        })
    )
);
